/**
 * Confirmation step of the "Configurar CD desde IaC" flow, rendered after the
 * server-side dry run resolves.
 *
 * The user sees a short summary of the values that will be applied — built by
 * the backend from the fields the CD template marks as infra-driven or
 * user-supplied — instead of the raw outputs dump this step used to show. The
 * internal detail (built cd_values, the generated infra_values.yaml, resolver
 * warnings, per-field failure details) is served by the backend only to
 * platform operators; this component therefore keys the "technical detail"
 * accordion off the *presence* of those fields rather than re-checking the
 * caller's role, so there is a single source of truth for the decision.
 */

import { Fragment, ReactNode } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';

/** One row of the confirmation summary, as returned by the dry run. */
export interface InfraCdSummaryRow {
  label: string;
  value: string;
}

/**
 * Dry-run response of `PUT /repositories/:repo/templates/from-infra`. Every
 * field except `summary`/`template` is present only for callers the backend
 * accepts as platform operators.
 */
export interface InfraCdPreview {
  summary?: InfraCdSummaryRow[];
  template?: { name: string; version: string };
  payload?: Record<string, any>;
  infraValuesPreview?: string;
  infraValuesPath?: string;
  warnings?: string[];
}

/** Failure of the dry run; `details` only arrives for platform operators. */
export interface InfraCdError {
  message: string;
  details?: string[];
}

const useStyles = makeStyles(theme => ({
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.5),
    alignItems: 'baseline',
    margin: 0,
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
  },
  summaryValue: {
    margin: 0,
    wordBreak: 'break-word',
  },
  reference: {
    marginTop: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  monospace: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  detailBlock: {
    margin: 0,
    fontSize: 12,
    maxHeight: 220,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
  },
  detailBody: {
    display: 'block',
  },
}));

/**
 * Collapsed accordion holding the operator-facing detail. Renders nothing when
 * the backend served no detail at all (i.e. the caller is a regular user).
 */
const TechnicalDetails = ({
  warnings,
  blocks,
}: {
  warnings?: string[];
  blocks: { caption: string; content: string }[];
}) => {
  const classes = useStyles();
  const hasWarnings = Array.isArray(warnings) && warnings.length > 0;
  if (!hasWarnings && blocks.length === 0) return null;

  return (
    <Accordion variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2">
          Ver detalle técnico <em>(solo plataforma)</em>
        </Typography>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.detailBody }}>
        {hasWarnings && (
          <Alert severity="info" style={{ marginBottom: 8 }}>
            {warnings!.join(' · ')}
          </Alert>
        )}
        {blocks.map(block => (
          <Box key={block.caption} mb={1}>
            <Typography variant="caption" color="textSecondary">
              {block.caption}
            </Typography>
            <pre className={classes.detailBlock}>{block.content}</pre>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

/** Shared shell: message, body, technical detail, actions. */
const StepPanel = ({
  alert,
  children,
  details,
  actions,
}: {
  alert: ReactNode;
  children?: ReactNode;
  details: ReactNode;
  actions?: ReactNode;
}) => (
  <Box style={{ marginTop: 8 }}>
    {alert}
    {children}
    {details}
    {actions && (
      <Box display="flex" justifyContent="flex-end" style={{ marginTop: 8 }}>
        {actions}
      </Box>
    )}
  </Box>
);

export interface InfraCdConfirmationProps {
  preview: InfraCdPreview;
  /**
   * Workflow id of the selected infra, shown as the support reference.
   * Addresses a single environment's launch, unlike `contextId` (stable per
   * Servicio de Aplicación and shared across every environment).
   */
  workflowId: string;
  saving: boolean;
  onSave: () => void;
}

export const InfraCdConfirmation = ({
  preview,
  workflowId,
  saving,
  onSave,
}: InfraCdConfirmationProps) => {
  const classes = useStyles();
  const rows = Array.isArray(preview.summary) ? preview.summary : [];
  const detailBlocks: { caption: string; content: string }[] = [];
  if (preview.payload) {
    detailBlocks.push({
      caption: 'Valores de configuración (cd_values)',
      content: JSON.stringify(
        preview.payload?.repositoryTemplate?.configValues ?? {},
        null,
        2,
      ),
    });
  }
  if (preview.infraValuesPreview) {
    detailBlocks.push({
      caption: `${preview.infraValuesPath ?? 'infra_values.yaml'} (secretos como referencias)`,
      content: preview.infraValuesPreview,
    });
  }

  return (
    <StepPanel
      alert={
        <Alert severity="success" style={{ marginBottom: 8 }}>
          Configuración de CD generada y validada a partir de la infraestructura
          seleccionada. Revisa los valores y pulsa "Guardar configuración" para
          aplicarla.
        </Alert>
      }
      details={
        <TechnicalDetails warnings={preview.warnings} blocks={detailBlocks} />
      }
      actions={
        <Button
          color="primary"
          variant="contained"
          onClick={onSave}
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </Button>
      }
    >
      <Paper variant="outlined" style={{ padding: 12, marginBottom: 8 }}>
        {rows.length > 0 ? (
          <dl className={classes.summaryGrid}>
            {rows.map(row => (
              <Fragment key={row.label}>
                <Typography
                  variant="body2"
                  component="dt"
                  className={classes.summaryLabel}
                >
                  {row.label}
                </Typography>
                <Typography
                  variant="body2"
                  component="dd"
                  className={classes.summaryValue}
                >
                  <b>{row.value}</b>
                </Typography>
              </Fragment>
            ))}
          </dl>
        ) : (
          <Typography variant="body2" color="textSecondary">
            La plantilla de CD no declara valores que resumir; la configuración
            se ha generado y validada correctamente.
          </Typography>
        )}
        <Box className={classes.reference}>
          <Typography variant="caption" color="textSecondary">
            Referencia de la infraestructura (cítala si necesitas soporte)
          </Typography>
          <Typography variant="body2" className={classes.monospace}>
            {workflowId}
          </Typography>
        </Box>
      </Paper>
    </StepPanel>
  );
};

export interface InfraCdErrorPanelProps {
  error: InfraCdError;
  workflowId: string;
  /** Absent when there is nothing to retry (the quick step never confirmed). */
  onRetry?: () => void;
  /**
   * Absent when the caller offers no manual fallback. When present, renders a
   * secondary "Configurar manualmente" action next to "Reintentar" so the user
   * is never stuck on a blocking error with no way forward.
   */
  onManualFallback?: () => void;
}

export const InfraCdErrorPanel = ({
  error,
  workflowId,
  onRetry,
  onManualFallback,
}: InfraCdErrorPanelProps) => {
  const classes = useStyles();
  const details = Array.isArray(error.details) ? error.details : [];

  return (
    <StepPanel
      alert={
        <Alert severity="error">
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            {error.message}
          </Typography>
          <Typography variant="body2" className={classes.monospace}>
            Referencia: {workflowId}
          </Typography>
        </Alert>
      }
      details={
        <TechnicalDetails
          blocks={
            details.length > 0
              ? [{ caption: 'Detalle de la resolución', content: details.join('\n') }]
              : []
          }
        />
      }
      actions={
        <>
          {onManualFallback && (
            <Button
              size="small"
              onClick={onManualFallback}
              style={{ marginRight: 8 }}
            >
              Configurar manualmente
            </Button>
          )}
          {onRetry && (
            <Button size="small" variant="outlined" onClick={onRetry}>
              Reintentar
            </Button>
          )}
        </>
      }
    />
  );
};
